// What is due, overdue or blocked for the signed-in person. Contributors land here.
import { Link, useOutletContext } from 'react-router-dom';
import { useDeliverables } from '@/lib/deliverables';
import { AwaitingDays } from './deliverables/DeliverablesPage';
import type { ReactNode } from 'react';
import { useMe } from '@/lib/auth';
import { isOverdue, useTracker, type Task, type TrackerData } from '@/lib/tracker';
import type { Program } from '@/lib/types';
import { fmtDate } from '@/lib/utils';
import { Card, PageHeader, Spinner } from '@/components/ui/misc';
import { DueDate, PhaseChip, StatusPill } from '@/components/tracker/bits';
import { useOpenTask } from '@/components/tracker/TaskPanel';

const addDays = (iso: string, n: number) =>
  new Date(new Date(`${iso}T00:00:00Z`).getTime() + n * 86400000).toISOString().slice(0, 10);

export function MyWork() {
  const program = useOutletContext<Program>();
  const { data: me } = useMe();
  const tracker = useTracker(program.id);
  if (!tracker.data || !me) return <Spinner className="h-full" />;
  const data = tracker.data;
  const mine = data.tasks.filter((t) => t.ownerId === me.user.id && t.status !== 'DONE');
  const weekOut = addDays(data.today, 7);

  const overdue = mine.filter((t) => isOverdue(t, data.today));
  const blocked = mine.filter((t) => t.status === 'BLOCKED' && !overdue.includes(t));
  const dueSoon = mine.filter((t) => !overdue.includes(t) && !blocked.includes(t) && t.dueDate && t.dueDate <= weekOut);
  const rest = mine.filter((t) => !overdue.includes(t) && !blocked.includes(t) && !dueSoon.includes(t));
  const byDue = (a: Task, b: Task) => (a.dueDate ?? '9999').localeCompare(b.dueDate ?? '9999');

  return (
    <div className="mx-auto max-w-4xl px-6 py-6">
      <PageHeader
        title="My work"
        description={
          mine.length
            ? `${mine.length} open task${mine.length === 1 ? '' : 's'} assigned to you · today ${fmtDate(data.today)}`
            : 'Nothing is assigned to you right now.'
        }
      />
      <div className="flex flex-col gap-5">
        <AwaitingAcceptance program={program} />
        <Group title="Overdue" tone="bad" tasks={overdue.sort(byDue)} data={data} empty="Nothing overdue." />
        <Group title="Blocked" tasks={blocked.sort(byDue)} data={data} />
        <Group title="Due in the next 7 days" tasks={dueSoon.sort(byDue)} data={data} empty="Nothing due this week." />
        <Group title="Everything else open" tasks={rest.sort(byDue)} data={data} />
      </div>
    </div>
  );
}

function Group({
  title,
  tasks,
  data,
  empty,
  tone,
}: {
  title: string;
  tasks: Task[];
  data: TrackerData;
  empty?: string;
  tone?: 'bad';
}) {
  const open = useOpenTask();
  if (!tasks.length && !empty) return null;
  return (
    <section>
      <h2
        className={
          tone === 'bad' && tasks.length ? 'mb-2 text-sm font-semibold text-bad' : 'mb-2 text-sm font-semibold'
        }
      >
        {title} <span className="font-normal text-subtle">· {tasks.length}</span>
      </h2>
      <Card>
        {tasks.length ? (
          <ul>
            {tasks.map((t) => {
              const phase = data.phases.find((p) => p.id === t.phaseId);
              return (
                <li key={t.id}>
                  <Row onClick={() => open(t.id)}>
                    {phase && <PhaseChip number={phase.number} name={phase.name} />}
                    <span className="min-w-0 flex-1 truncate">{t.title}</span>
                    <StatusPill status={t.status} />
                    <DueDate date={t.dueDate} overdue={isOverdue(t, data.today)} className="w-24 text-right text-xs" />
                  </Row>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="px-4 py-3 text-[13px] text-muted">{empty}</p>
        )}
      </Card>
    </section>
  );
}

function Row({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return (
    <button
      className="flex w-full items-center gap-3 border-b border-border px-4 py-2.5 text-left text-[13px] last:border-0 hover:bg-surface-2/50"
      onClick={onClick}
    >
      {children}
    </button>
  );
}

/** Deliverables waiting for this person's acceptance (shown only when there are any). */
function AwaitingAcceptance({ program }: { program: Program }) {
  const q = useDeliverables(program.id);
  const mine = q.data?.filter((d) => d.canAccept) ?? [];
  if (!mine.length) return null;
  return (
    <section>
      <h2 className="mb-2 text-sm font-semibold text-warn">
        Awaiting your acceptance <span className="font-normal text-subtle">· {mine.length}</span>
      </h2>
      <Card>
        <ul>
          {mine.map((d) => (
            <li key={d.id}>
              <Link
                to={`/p/${program.id}/deliverables/${d.id}`}
                className="flex items-center gap-3 border-b border-border px-4 py-2.5 text-[13px] last:border-0 hover:bg-surface-2/50"
              >
                <PhaseChip number={d.phase.number} name={d.phase.name} />
                <span className="min-w-0 flex-1 truncate">{d.name}</span>
                <span className="text-xs text-muted">v{d.currentVersion}</span>
                <span className="w-28 text-right text-xs">
                  <AwaitingDays days={d.daysAwaiting} /> waiting
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </Card>
    </section>
  );
}
